import React from 'react';
import { Modal, Button, Message } from 'semantic-ui-react';
import { Formik, Form as FormikForm, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import SemanticFormikInput from '@common/Forms/SemanticFormikInput';
import { UserClient } from '@app/api2/api';
import { BPButton } from '@/components/bp/bpbutton';

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const ChangePasswordSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current password is required'),
    newPassword: Yup.string().min(8, 'Password must be at least 8 characters').required('New password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Confirm your new password'),
});

export const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({
    open,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const userClient = new UserClient();

    return (

        <Formik
            initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
            validationSchema={ChangePasswordSchema}
            onSubmit={async (values, { setSubmitting, resetForm }: FormikHelpers<any>) => {
                setSubmitting(true);
                setLoading(true);
                setError(null);
                try {
                    await userClient.changePassword({ currentPassword: values.currentPassword, password: values.newPassword, confirmPassword: values.confirmPassword });
                    resetForm({});
                    if (onSuccess) onSuccess();
                    onClose();
                } catch (err: any) {
                    // console.log(JSON.stringify(err));
                    setError(err.changePasswordErrors || 'Failed to change password');
                } finally {
                    setSubmitting(false);
                    setLoading(false);
                }
            }}
        >
            {({ isSubmitting, resetForm }) => (
                <Modal as={FormikForm} open={open} className="ui form"
                    closeOnDimmerClick={false} closeOnDocumentClick={false}
                    onClose={() => { onClose(); resetForm({}); }}>
                    <Modal.Header>Change Password</Modal.Header>

                    <Modal.Content>
                        <SemanticFormikInput
                            name="currentPassword"
                            type="password"
                            label="Current Password"
                            autoComplete="current-password"
                            required
                        />
                        <SemanticFormikInput
                            name="newPassword"
                            type="password"
                            label="New Password"
                            autoComplete="new-password"
                            required
                        />
                        <SemanticFormikInput
                            name="confirmPassword"
                            type="password"
                            label="Confirm New Password"
                            autoComplete="new-password"
                            required
                        />
                        {error && <Message negative>{error}</Message>}
                    </Modal.Content>
                    <Modal.Actions>
                        <BPButton type="button" onClick={() => { onClose(); resetForm({}); }} disabled={isSubmitting || loading}>
                            Cancel
                        </BPButton>
                        <BPButton
                            type="submit"
                            primary
                            loading={isSubmitting || loading}
                            disabled={isSubmitting || loading}>
                            Change Password
                        </BPButton>
                    </Modal.Actions>
                </Modal>
            )}
        </Formik>
    );
};
